import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Icon, Tooltip } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import ToastrAction from 'toastr/actions/ToastrAction'
import useSandreList from 'utils/customHook/useSandreList'
import { compact, groupBy, keys, orderBy } from 'lodash'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Select from 'components/forms/Select'
import Checkbox from 'components/forms/Checkbox'
import { hasValue } from 'utils/NumberUtil'
import Input from 'components/forms/Input'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import useSandreListIndexed from 'utils/customHook/useSandreListIndexed'

const OTHERS = -1

const ActivityDialog = ({
    selectedActivity = {},
    isOpen = false,
    setIsOpen = () => { },
    onChange = () => { },
}) => {
    const dispatch = useDispatch()

    const [activity, setActivity] = useState(selectedActivity)
    const activities = useSandreList(SANDRE.PGSSE_ACTIVITES)

    useEffect(()=> {
        setActivity(selectedActivity)
    }, [selectedActivity])

    const onChangeActivity = values => setActivity(prev => ({ ...prev, ...values }))

    return (
        <Dialog open={isOpen} maxWidth='lg'>
            <DefaultDialogTitle title={selectedActivity.index ? i18n.newActivity : i18n.editActivity} onClose={() => setIsOpen(false)} />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={1} alignItems='center'>
                            <Grid size={6}>
                                <Select
                                    label={i18n.category}
                                    value={activity.category}
                                    options={activities}
                                    onChange={v => onChangeActivity({ category: v })}
                                    disabled={activity.lexId !== OTHERS}
                                />
                            </Grid>
                            <Grid size={6}>
                                {
                                    activity.lexId === OTHERS && (
                                        <Input
                                            title={i18n.name}
                                            value={activity.otherActivityName}
                                            onChange={v => onChangeActivity({ otherActivityName: v })}
                                            obligatory
                                        />
                                    ) || (
                                        <Select
                                            label={i18n.refDoc}
                                            value={activity.lexId}
                                            options={activities}
                                            disabled
                                        />
                                    )
                                }

                            </Grid>
                            <Grid size={6}>
                                <Checkbox
                                    label={i18n.regie}
                                    checked={activity.regie}
                                    onChange={v => onChangeActivity({ regie: v })}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Checkbox
                                    label={i18n.delegation}
                                    checked={activity.delegation}
                                    onChange={v => onChangeActivity({ delegation: v })}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SimpleTextArea
                                    title={i18n.comment}
                                    value={activity.comment}
                                    onChange={v => onChangeActivity({ comment: v })}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (activity.lexId === OTHERS && !activity.otherActivityName) {
                            dispatch(ToastrAction.error(i18n.pleaseCompleteAllRequiredField))
                        } else {
                            onChange({ ...activity, toUpdate: true })
                            setIsOpen(false)
                            setActivity({})
                        }
                    }}
                >
                    {i18n.register}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ActivityDialog.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    onChange: PropTypes.func,
    selectedActivity: PropTypes.object, // DtoPgsseActivity
}


const ActivitiesPanel = ({
    link_activities = [],
    onChange,
    readMode,
}) => {
    const headers = ['category', { value: i18n.skills, key: 'refDoc' }, 'regie', { value: i18n.delegationHeader, key: 'delegation' }, 'comment']

    const dispatch = useDispatch()

    const {
        sandreCodes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const [isOpen, setIsOpen] = useState(false)
    const [selectedActivity, setSelectedActivity] = useState({})

    const actLexicons = useMemo(() => {
        const filteredSandreCodes = sandreCodes.filter(sc => sc.field.startsWith(`${SANDRE.PGSSE_ACTIVITES}.`))
        return groupBy(filteredSandreCodes, sc => sc.field.split('.')[2])
    }, [sandreCodes])

    const activities = keys(actLexicons).flatMap(key => {
        const acts = actLexicons[key]
        return acts.map(act => ({
            ...link_activities.find(la => la.lexId === act.code && la.category === parseInt(key)),
            category: parseInt(key),
            lexId: act.code,
            name: act.name,
        }))
    })

    const indexedElements = orderBy([
        ...activities,
        ...link_activities.filter(d => d.lexId === OTHERS),
    ].map((d, index) => ({ ...d, index })), 'category')

    const sandreActs = useSandreListIndexed(SANDRE.PGSSE_ACTIVITES)

    const rows = indexedElements.map((d) => ({
        ...d,
        category: sandreActs[d.category]?.name,
        refDoc: d.name || d.otherActivityName,
        regie: { value: d.regie && <Icon>check_value</Icon>, sortValue: d.regie },
        delegation: { value: d.delegation && <Icon>check_value</Icon>, sortValue: d.delegation },
        comment: { value: d.comment && <Tooltip title={d.comment}><Icon>information</Icon></Tooltip>, sortValue: d.comment },
    }))

    const updateElements = (newElement) => {
        const newLinkActivities = (() => {
            if (!hasValue(newElement.index)) {
                return [...indexedElements, newElement]
            }
            return indexedElements.map((d) => d.index === newElement.index ? newElement : d)
        })()

        onChange({ link_activities: newLinkActivities.filter(l => l.toUpdate) })
    }

    const actions = compact([{
        icon: 'download',
        tooltip: i18n.export,
        onClick: () => {
            dispatch(ToastrAction.error(i18n.inDeveloppmentFunctionnality))
        },
    }, !readMode && {
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => {
            setSelectedActivity({ lexId: OTHERS })
            setIsOpen(true)
        },
    }])

    const lineActions = [{
        icon: 'close',
        onClick: (element) => onChange({ link_activities: indexedElements.filter(l => l.toUpdate && l.index !== element.index) }),
        displayed: !readMode,
    }, {
        icon: 'edit',
        onClick: (element) => {
            setSelectedActivity(indexedElements.find(l => element.index === l.index))
            setIsOpen(true)
        },
        displayed: !readMode,
    }]

    return (
        <>
            <CardTable
                title={i18n.activities}
                rows={rows}
                headers={headers}
                lineActions={lineActions}
                actions={actions}
            />
            <ActivityDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onChange={updateElements}
                selectedActivity={selectedActivity}
            />
        </>
    )
}

ActivitiesPanel.propTypes = {
    link_activities: PropTypes.arrayOf(PropTypes.shape({})),
    onChange: PropTypes.func,
    readMode: PropTypes.bool,
}

export default ActivitiesPanel